/**
 * Pairs Well With carousel.
 *
 * Live parity: desktop/tablet is a Swiper, mobile (<=749px) is a static 2-col
 * grid laid out in CSS, so the slider is torn down there rather than resized.
 *
 * Swiper init follows the project standard: check whether Swiper is already
 * available in connectedCallback, and fall back to the script's `load` event
 * so the carousel recovers even when swiper14.0.6.min.js lands after the
 * element is upgraded.
 */
if (!customElements.get('pairs-well-with-carousel')) {
  class PairsWellWithCarousel extends HTMLElement {
    static MOBILE_QUERY = '(max-width: 749px)';

    swiper = null;
    #onResize = () => this.initSwiper();
    #onSwiperLoad = () => this.initSwiper();

    connectedCallback() {
      this.setupEventListeners();

      if (typeof Swiper !== 'undefined') {
        this.initSwiper();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.#onResize);
      document.getElementById('swiper-script')?.removeEventListener('load', this.#onSwiperLoad);
      this.destroySwiper();
    }

    setupEventListeners() {
      window.addEventListener('resize', this.#onResize);
      document.getElementById('swiper-script')?.addEventListener('load', this.#onSwiperLoad);
    }

    destroySwiper() {
      this.swiper?.destroy?.(true, true);
      this.swiper = null;
    }

    initSwiper() {
      const carouselEl = this.querySelector('.pairs-well-with__carousel');
      if (!carouselEl) return;

      if (window.matchMedia(PairsWellWithCarousel.MOBILE_QUERY).matches) {
        this.destroySwiper();
        return;
      }

      if (this.swiper) return;

      const columnsDesktop = parseInt(this.dataset.columnsDesktop, 10) || 3;

      this.swiper = new Swiper(carouselEl, {
        slidesPerView: 2,
        spaceBetween: 10,
        breakpoints: {
          750: { slidesPerView: 2, spaceBetween: 10 },
          990: { slidesPerView: 3, spaceBetween: 10 },
          1024: { slidesPerView: columnsDesktop, spaceBetween: 10 },
        },
      });
    }
  }

  customElements.define('pairs-well-with-carousel', PairsWellWithCarousel);
}

/**
 * Owns interactions for the Pairs Well With section.
 */
if (!customElements.get('pairs-well-with')) {
  class PairsWellWith extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    /**
     * The current-item card already points at the active product. Scroll to the
     * buy box instead of requesting the same page again.
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    onClick(event) {
      const link = event.target.closest('.pairs-well-with__current-link');
      if (!link) return;

      const targetPath = new URL(link.href, window.location.origin).pathname;
      if (targetPath !== window.location.pathname) return;

      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  customElements.define('pairs-well-with', PairsWellWith);
}
